import { logger } from '@/core/logger';
import { normalizeSalesforceStatus } from '@/core/reservation/mapSalesforceReservationStatus';
import {
  fetchAdhaNamespaces,
  fetchAdhaReservationStatusNamespaces,
} from './contentfulClient';
import { normalizeLocaleNamespace } from './localeNamespace';

export type AdhaTranslationLocale = 'en' | 'ar';
export type AdhaTranslationTree = Record<string, unknown>;

const BLOCKED_PATH_SEGMENTS = new Set(['__proto__', 'constructor', 'prototype']);

function setNestedValue(
    target: AdhaTranslationTree,
    path: string[],
    value: string,
): void {
    if (path.length === 0 || path.some((part) => BLOCKED_PATH_SEGMENTS.has(part))) {
        return;
    }

    let cursor: Record<string, unknown> = target;
    path.forEach((part, index) => {
        const isLeaf = index === path.length - 1;
        if (isLeaf) {
            cursor[part] = value;
            return;
        }

        const existing = cursor[part];
        if (!existing || typeof existing !== 'object' || Array.isArray(existing)) {
            cursor[part] = {};
        }
        cursor = cursor[part] as Record<string, unknown>;
    });
}

/**
 * Extract the key from a Contentful locale entry field.
 */
function getLocaleKey(fields: Record<string, any>): string {
    if (typeof fields.key === 'string') {
        return fields.key.trim();
    }
    if (typeof fields.title === 'string') {
        return fields.title.trim();
    }
    return '';
}

/**
 * Extract the raw namespace string from a resolved Contentful type reference.
 */
function getRawNamespace(resolvedType?: Record<string, any>): string {
    if (!resolvedType) return '';
    if (typeof resolvedType.value === 'string') {
        return resolvedType.value;
    }
    if (typeof resolvedType.title === 'string') {
        return resolvedType.title;
    }
    return '';
}

/**
* Convert tagged Contentful `locales` entries into the nested translation shape
* consumed by dotted keys:
*
* type=bookAppointment + key=title
* => { bookAppointment: { title: "Book an Appointment" } }
*
* type=auth + key=adhaUaePass.errorGeneric
* => { auth: { adhaUaePass: { errorGeneric: "..." } } }
*/
export function transformAdhaLocalesToTree(raw: unknown): AdhaTranslationTree {
    const response = raw as {
        items?: Array<Record<string, any>>;
        includes?: { Entry?: Array<Record<string, any>> };
    };
    const includeById = new Map<string, Record<string, any>>();

    for (const entry of response.includes?.Entry ?? []) {
        const id = entry.sys?.id;
        if (typeof id === 'string') includeById.set(id, entry);
    }

    const translations: AdhaTranslationTree = {};
    for (const item of response.items ?? []) {
        if (item.sys?.contentType?.sys?.id !== 'locales') continue;

        const fields = item.fields ?? {};
        const key = getLocaleKey(fields);
        const value = typeof fields.value === 'string' ? fields.value : undefined;
        const typeReference = fields.type;
        const resolvedType =
            typeReference?.fields ??
            (typeof typeReference?.sys?.id === 'string'
                ? includeById.get(typeReference.sys.id)?.fields
                : undefined);
        const rawNamespace = getRawNamespace(resolvedType);
        const namespace = rawNamespace
            ? normalizeLocaleNamespace(rawNamespace)
            : '';

        if (!namespace || !key || value === undefined) {
            logger.warn('adhaTranslationService', 'Skipped invalid locale entry', {
                entryId: item.sys?.id,
                hasNamespace: Boolean(namespace),
                hasKey: Boolean(key),
                hasValue: value !== undefined,
            });
            continue;
        }

        const keyParts = key
            .split('.')
            .map((part: string) => part.trim())
            .filter(Boolean)
            .map((part: string) =>
                namespace === 'reservationStatus'
                    ? normalizeSalesforceStatus(part)
                    : part,
            )
            .filter(Boolean);

        setNestedValue(translations, [namespace, ...keyParts], value);
    }

    return translations;
}

/** Fetch and transform only Contentful `locales` entries tagged `adha`. */
export async function fetchAdhaTranslations(
    locale: AdhaTranslationLocale,
): Promise<AdhaTranslationTree> {
    const result = await fetchAdhaNamespaces(locale);
    const translations = transformAdhaLocalesToTree(result.data);

    logger.info('adhaTranslationService', 'ADHA translations loaded', {
        locale,
        namespaces: Object.keys(translations),
        // data: translations,
    });

    return { adha: translations };
}

function listReservationStatusKeys(
  translations: AdhaTranslationTree,
): ReadonlyArray<string> {
  const reservationStatus = translations.reservationStatus;
  if (!reservationStatus || typeof reservationStatus !== 'object') {
    return [];
  }
  return Object.keys(reservationStatus as object);
}

/**
 * Fetch locales tagged `adhaReservationStatus` and map to
 * `{ adha: { reservationStatus: { "<normalized>": "...", default: "..." } } }`.
 */
export async function fetchAdhaReservationStatusTranslations(
  locale: AdhaTranslationLocale,
): Promise<AdhaTranslationTree> {
  const result = await fetchAdhaReservationStatusNamespaces(locale);
  const translations = transformAdhaLocalesToTree(result.data);

  logger.info('adhaTranslationService', 'ADHA reservationStatus locales loaded', {
    locale,
    namespaces: Object.keys(translations),
    reservationStatusKeys: listReservationStatusKeys(translations),
  });

  return { adha: translations };
}
